import React, { useCallback, useState } from "react"

import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  SearchInput,
} from "@coval/ui"
import { ListFilter } from "lucide-react"

import { useTestSetEditorContext } from "../contexts/TestSetEditorContext"
import { formatTestCaseTypeLabel } from "../utils/filterTestCases"

/**
 * Search input + test-case-type filter for the test set editor toolbar.
 * Reads and writes the view-only filter state on TestSetEditorContext.
 */
export const TestCaseFilterControls: React.FC = () => {
  const {
    testCaseSearchQuery,
    setTestCaseSearchQuery,
    testCaseTypeFilters,
    setTestCaseTypeFilters,
    availableTestCaseTypes,
  } = useTestSetEditorContext()
  const [isTypeMenuOpen, setIsTypeMenuOpen] = useState(false)

  const toggleType = useCallback(
    (type: string) => {
      setTestCaseTypeFilters(
        testCaseTypeFilters.includes(type)
          ? testCaseTypeFilters.filter((value) => value !== type)
          : [...testCaseTypeFilters, type],
      )
    },
    [testCaseTypeFilters, setTestCaseTypeFilters],
  )

  const selectedCount = testCaseTypeFilters.length

  return (
    <div className="flex items-center gap-2">
      <SearchInput
        aria-label="Search test cases"
        placeholder="Search test cases..."
        value={testCaseSearchQuery}
        onChange={(e) => setTestCaseSearchQuery(e.target.value)}
        onClear={() => setTestCaseSearchQuery("")}
        className="w-64"
      />
      {availableTestCaseTypes.length > 1 && (
        <DropdownMenu open={isTypeMenuOpen} onOpenChange={setIsTypeMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="secondary"
              active={selectedCount > 0}
              className="px-2.5"
              aria-label="Filter test cases by type"
            >
              <ListFilter className="w-4 h-4" />
              Type
              {selectedCount > 0 && (
                <span className="ml-0.5 rounded-sm bg-surface-default px-1 text-xs text-coval-text-muted">
                  {selectedCount}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-48">
            {availableTestCaseTypes.map((type) => {
              const isSelected = testCaseTypeFilters.includes(type)
              return (
                <DropdownMenuItem
                  key={type}
                  onSelect={(e) => {
                    e.preventDefault()
                    toggleType(type)
                  }}
                  className="flex items-center gap-2"
                >
                  <Checkbox checked={isSelected} tabIndex={-1} aria-hidden />
                  <span className="flex-1">
                    {formatTestCaseTypeLabel(type)}
                  </span>
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
